// src/plan/Plan.jsx
import { useState, useEffect } from "react";
import PageHeader from "../components/PageHeader";
import AppModal from "../components/AppModal";
import CustomDataTable from "../components/DataTable";
import { FiEdit, FiTrash2, FiPlus, FiDollarSign, FiUsers, FiCheckCircle, FiXCircle } from "react-icons/fi";
import "./Plans.css";

export default function Plans({ darkMode }) {
    const [show, setShow] = useState(false);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Mock data - replace with API call
    useEffect(() => {
        // Simulate API fetch
        setLoading(true);
        setTimeout(() => {
            setPlans([
                {
                    id: 1,
                    name: "Basic",
                    amount: 99,
                    status: "Active",
                    description: "Basic plan for beginners",
                    features: ["Feature 1", "Feature 2"],
                    users: 45,
                },
                {
                    id: 2,
                    name: "Premium",
                    amount: 199,
                    status: "Active",
                    description: "Premium plan for professionals",
                    features: ["Feature 1", "Feature 2", "Feature 3"],
                    users: 28,
                },
                {
                    id: 3,
                    name: "Pro",
                    amount: 499,
                    status: "Inactive",
                    description: "Pro plan for enterprises",
                    features: ["Feature 1", "Feature 2", "Feature 3", "Feature 4"],
                    users: 12,
                },
            ]);
            setLoading(false);
        }, 500);
    }, []);

    const columns = [
        {
            name: "Plan Name",
            selector: row => row.name,
            sortable: true,
            cell: row => <span className="plan-name">{row.name}</span>,
        },
        {
            name: "Amount",
            selector: row => `₹${row.amount}`,
            sortable: true,
            cell: row => <span className="plan-amount">₹{row.amount}</span>,
        },
        {
            name: "Users",
            selector: row => row.users || 0,
            sortable: true,
            cell: row => <span className="plan-users">{row.users || 0}</span>,
        },
        {
            name: "Status",
            cell: row => (
                <span className={`status-badge ${row.status === "Active" ? "active" : "inactive"}`}>
                    {row.status}
                </span>
            ),
        },
        {
            name: "Actions",
            cell: row => (
                <div className="action-buttons">
                    <button 
                        className="action-btn edit" 
                        onClick={() => handleEdit(row)}
                        title="Edit Plan"
                    >
                        <FiEdit size={14} />
                    </button>
                    <button 
                        className="action-btn delete" 
                        onClick={() => handleDeleteClick(row)}
                        title="Delete Plan"
                    >
                        <FiTrash2 size={14} />
                    </button>
                </div>
            ),
        },
    ];

    // Handle edit plan
    const handleEdit = (plan) => {
        setSelectedPlan(plan);
        setShow(true);
    };

    // Handle delete click
    const handleDeleteClick = (plan) => {
        setSelectedPlan(plan);
        setShowDeleteModal(true);
    };

    // Handle delete plan
    const handleDelete = () => {
        if (selectedPlan) {
            setPlans(prev => prev.filter(p => p.id !== selectedPlan.id));
            setShowDeleteModal(false);
            setSelectedPlan(null);
        }
    };

    // Handle add plan
    const handleAddPlan = () => {
        setSelectedPlan(null);
        setShow(true);
    };

    // Filter plans based on search
    const filteredData = plans.filter(item =>
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description?.toLowerCase().includes(search.toLowerCase())
    );

    // Get counts for stats
    const totalPlans = plans.length;
    const activePlans = plans.filter(p => p.status === "Active").length;
    const inactivePlans = plans.filter(p => p.status !== "Active").length;
    const totalUsers = plans.reduce((acc, p) => acc + (p.users || 0), 0);

    return (
        <div className={`plans-container ${darkMode ? 'dark' : ''}`}>
            <div className="plans-content">
                {/* Page Header */}
                <PageHeader
                    title="Plans"
                    buttonText="Add Plan"
                    onAdd={handleAddPlan}
                />

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon total">
                            <FiDollarSign size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Total Plans</span>
                            <span className="stat-value">{totalPlans}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon active">
                            <FiCheckCircle size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Active Plans</span>
                            <span className="stat-value">{activePlans}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon inactive">
                            <FiXCircle size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Inactive Plans</span>
                            <span className="stat-value">{inactivePlans}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon users">
                            <FiUsers size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Total Users</span>
                            <span className="stat-value">{totalUsers}</span>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="table-card-modern">
                    {/* Search */}
                    <div className="table-toolbar">
                        <div className="search-wrapper">
                            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                            <input
                                className="search-input-modern"
                                placeholder="Search plans by name or description..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="table-info">
                            Showing {filteredData.length} of {plans.length} plans
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-wrapper-modern">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading plans...</p>
                            </div>
                        ) : (
                            <CustomDataTable
                                columns={columns}
                                data={filteredData}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            <AppModal
                show={show}
                title={selectedPlan ? "Edit Plan" : "Add Plan"}
                onClose={() => {
                    setShow(false);
                    setSelectedPlan(null);
                }}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedPlan && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Delete Plan</h3>
                            <button className="modal-close-btn" onClick={() => setShowDeleteModal(false)}>
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirm-text">
                                Are you sure you want to delete the plan <strong>"{selectedPlan.name}"</strong>?
                            </p>
                            <p className="delete-warning-text">
                                This action cannot be undone. All associated data will be removed.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-cancel-btn" onClick={() => setShowDeleteModal(false)}>
                                Cancel
                            </button>
                            <button className="modal-delete-btn" onClick={handleDelete}>
                                Delete Plan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}